import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import Brand from './Brand';
import {
  GET_STARTER_PACKS,
  INSTALL_STARTER_PACK,
  MARK_ONBOARDED,
} from '../graphql/starter-pack-queries';

/**
 * Onboarding — first-run experience for teachers who haven't completed onboarding.
 * Offers HSK starter packs (HSK 1 free, HSK 2+ pro). Both "install pack" and
 * "skip for now" mark the teacher as onboarded server-side so the screen doesn't reappear.
 */
function Onboarding({ teacher, onComplete, onSkip }) {
  const { data, loading, error } = useQuery(GET_STARTER_PACKS);
  const [installPack, { loading: installing }] = useMutation(INSTALL_STARTER_PACK);
  const [markOnboarded] = useMutation(MARK_ONBOARDED);
  const [activePackId, setActivePackId] = useState(null);
  const [installError, setInstallError] = useState(null);

  const handleSkip = useCallback(async () => {
    // Best-effort persist; navigate even if it fails so the user isn't stuck.
    try { await markOnboarded(); } catch { /* ignore */ }
    onSkip();
  }, [markOnboarded, onSkip]);

  const isPro = teacher?.plan === 'pro';
  const packs = data?.starterPacks || [];

  const activePack = packs.find((p) => p.id === activePackId);

  const handleInstall = async (pack) => {
    if (pack.proOnly && !isPro) return;
    setActivePackId(pack.id);
    setInstallError(null);
    try {
      await installPack({ variables: { packId: pack.id } });
      // The mutation typically returns in ~50ms which is too fast to
      // perceive as "doing something". Hold the spinner for a beat so the
      // setup screen reads as a deliberate moment, then advance.
      setTimeout(() => onComplete(), 1400);
    } catch (err) {
      setInstallError(err.message);
      setActivePackId(null);
    }
  };

  // While a pack is installing, replace the picker with a dedicated loading
  // screen. We gate on `activePackId` (not `installing`) because the mutation
  // can complete in ~50ms — too fast to see — but onComplete fires 300ms
  // later, so we want the spinner up the whole time. activePackId is reset
  // to null only on error; on success the parent unmounts this component.
  if (activePackId) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}>
        <div
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            border: '3px solid var(--rule)',
            borderTopColor: 'var(--vermillion)',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
            marginBottom: 28,
          }}
        />
        <div className="t-eyebrow" style={{ marginBottom: 12 }}>Setting up · 准备中</div>
        <h1 className="t-h1" style={{ marginBottom: 8, textAlign: 'center', maxWidth: 480 }}>
          Installing {activePack?.title || 'your starter pack'}…
        </h1>
        <p className="t-body" style={{ textAlign: 'center', maxWidth: 480, color: 'var(--ink-2)' }}>
          Adding {activePack?.wordCount ?? ''} words to your library. This only takes a moment.
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <Brand size="sm" />
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          Welcome · 入门
        </span>
        <button className="btn btn--ghost btn--sm" onClick={handleSkip}>
          Skip for now
        </button>
      </header>

      <div
        className="ink-classic-shell"
        style={{ maxWidth: 880, margin: '0 auto', padding: '64px 32px' }}
      >
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div
            className="t-eyebrow"
            style={{ marginBottom: 16, color: 'var(--vermillion)' }}
          >
            Your first vocabulary pack
          </div>
          <h1 className="t-display" style={{ marginBottom: 12 }}>
            Pick a pack to start.
          </h1>
          <p className="t-body" style={{ fontSize: 17, maxWidth: 560, margin: '0 auto' }}>
            We'll seed your library with a curated set so you can dive into Classic, Reading,
            and Quiz Battle right away. You can edit or replace any of these later.
          </p>
        </div>

        {loading && <p className="t-body" style={{ textAlign: 'center' }}>Loading packs…</p>}

        {error && (
          <div
            className="card"
            style={{
              padding: 24,
              textAlign: 'center',
              borderColor: 'var(--vermillion)',
              background: 'var(--vermillion-50)',
            }}
          >
            <p className="t-body" style={{ color: 'var(--vermillion-ink)' }}>
              Couldn't load starter packs: {error.message}
            </p>
          </div>
        )}

        {/* Pack grid */}
        {!loading && !error && (
          <div
            className="ink-admin-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginBottom: 32,
            }}
          >
            {packs.map((pack) => {
              const locked = pack.proOnly && !isPro;
              const isInstalling = activePackId === pack.id && installing;
              return (
                <div
                  key={pack.id}
                  style={{
                    position: 'relative',
                    background: locked ? 'var(--paper-2)' : 'var(--card)',
                    border: '1px solid var(--rule)',
                    borderRadius: 12,
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 240,
                    opacity: locked ? 0.7 : 1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 16,
                    }}
                  >
                    <span className="badge badge--mono">HSK {pack.hskLevel}</span>
                    {pack.proOnly ? (
                      <span className="badge badge--gold">Pro</span>
                    ) : (
                      <span className="badge badge--jade">Free</span>
                    )}
                  </div>

                  <h2 className="t-h2" style={{ marginBottom: 4 }}>
                    {pack.title}
                  </h2>
                  <div
                    className="pinyin"
                    style={{ fontSize: 14, marginBottom: 12 }}
                  >
                    {pack.subtitle}
                  </div>
                  <p
                    className="t-small"
                    style={{
                      flex: 1,
                      marginBottom: 16,
                      lineHeight: 1.5,
                      color: 'var(--ink-2)',
                    }}
                  >
                    {pack.description}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderTop: '1px solid var(--rule-2)',
                    }}
                  >
                    <span className="t-small">
                      <span className="t-mono">{pack.wordCount}</span> words
                    </span>
                    {locked ? (
                      <span className="t-small" style={{ color: 'var(--gold)' }}>
                        Pro only
                      </span>
                    ) : (
                      <button
                        className="btn btn--accent btn--sm"
                        onClick={() => handleInstall(pack)}
                        disabled={isInstalling || installing}
                      >
                        {isInstalling ? 'Installing…' : 'Install →'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {installError && (
          <p
            style={{
              textAlign: 'center',
              color: 'var(--error)',
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {installError}
          </p>
        )}

        {/* Free-tier hint */}
        {!isPro && (
          <div
            style={{
              padding: 16,
              background: 'var(--paper-2)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r3)',
              textAlign: 'center',
              marginBottom: 16,
            }}
          >
            <p className="t-small" style={{ margin: 0 }}>
              Free plan: HSK 1 only · 50-word library cap. Upgrade to Pro for HSK 2–6 and
              unlimited words.
            </p>
          </div>
        )}

        {/* Skip + manual route */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            type="button"
            onClick={handleSkip}
            style={{
              background: 'transparent',
              border: 0,
              color: 'var(--ink-3)',
              cursor: 'pointer',
              fontSize: 13,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              fontFamily: 'var(--font-sans)',
            }}
          >
            Or build my library from scratch
          </button>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
